import React, { useEffect, useRef, useState } from 'react';
import CustomMenu from '@app/components/CustomMenu';
import { INDEXDB_COMMON_KEYS } from '@app/consts/common_consts';
import { Cache } from '@app/modules/cache';
import { i18n, LanguageCode } from '@app/modules/i18n-locales';
import { Button } from 'primereact/button';
import jpIcon from '@src/assets/imgs/Country--japan.svg';
import ukIcon from '@src/assets/imgs/Country--uk.svg';
import translateIcon from '@src/assets/imgs/icons/Translate.svg';

interface LanguageSwitcherProps {
  className?: string;
  buttonClassName?: string;
}

const LanguageSwitcher: React.FC<LanguageSwitcherProps> = ({ className, buttonClassName }) => {
  const menuRef = useRef<any>(null);
  const [currentLang, setCurrentLang] = useState(i18n.language);

  // Load saved language preference on mount
  useEffect(() => {
    const loadLanguagePreference = async () => {
      try {
        const { success, data } = await Cache.get(INDEXDB_COMMON_KEYS.LANGUAGE_PREFERENCE, 'indexdb');
        if (success && data?.data) {
          const savedLang = data.data as string;
          if (savedLang !== i18n.language) {
            await i18n.changeLanguage(savedLang);
            setCurrentLang(savedLang);
          }
        }
      } catch (error) {
        console.error('Failed to load language preference:', error);
      }
    };

    loadLanguagePreference();
  }, []);

  // Update currentLang when i18n language changes
  useEffect(() => {
    const handleLanguageChange = (lang: string) => {
      setCurrentLang(lang);
    };

    i18n.on('languageChanged', handleLanguageChange);
    return () => {
      i18n.off('languageChanged', handleLanguageChange);
    };
  }, []);

  const switchLanguage = async (lang: string) => {
    try {
      await i18n.changeLanguage(lang);
      // Save language preference to IndexDB cache
      await Cache.upsert(INDEXDB_COMMON_KEYS.LANGUAGE_PREFERENCE, lang, 'indexdb', null);
      // Also sync to localStorage so i18next LanguageDetector picks it up after reload
      localStorage.setItem('i18nextLng', lang);
      setCurrentLang(lang);
    } catch (error) {
      console.error('Failed to save language preference:', error);
    }
  };

  // Build menu items fresh each render so labels/disabled always reflect currentLang
  const getLanguageItems = () => [
    {
      label: 'EN',
      icon: <img src={ukIcon} alt="English" className="mr-2" />,
      command: () => switchLanguage(LanguageCode.en),
      disabled: currentLang === LanguageCode.en,
    },
    {
      label: 'JP',
      icon: <img src={jpIcon} alt="Japanese" className="mr-2" />,
      command: () => switchLanguage(LanguageCode.ja),
      disabled: currentLang === LanguageCode.ja,
    },
  ];

  const toggleLanguageMenu = (event: React.MouseEvent<HTMLButtonElement>) => {
    menuRef.current?.toggle(event);
  };

  return (
    <>
      <Button
        icon={<img src={translateIcon} alt="translate" />}
        rounded
        text
        severity="secondary"
        className={buttonClassName || 'mr-2'}
        onClick={toggleLanguageMenu}
      />
      <CustomMenu
        ref={menuRef}
        model={getLanguageItems()}
        popup
        popupAlignment="right"
        className={className || 'language-menu'}
      />
    </>
  );
};

export default LanguageSwitcher;
